import { cn } from '@/common/utils/cn'
import { GovernmentAllocationCardDetails } from '@/features/privateCompanies/components/GovernmentAllocationCardDetails'
import { companyGovernmentAllocationStatusStyle } from '@/features/privateCompanies/constants/companyGovernmentAllocationStatusStyle.constant'
import {
  getBarLeftPosition,
  getCardLeftPosition,
  getCardRightPosition,
} from '@/features/privateCompanies/services/getLayoutGovernmentAllocation'
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { isAfter, isSameMonth, isWithinInterval } from 'date-fns'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

type GovernmentAllocationMonthDetailsProps = {
  governmentAllocation: CompanyGovernmentAllocationData
  year: number
  month: number
}

export const GovernmentAllocationMonthDetails = ({
  governmentAllocation,
  year,
  month,
}: GovernmentAllocationMonthDetailsProps) => {
  const { id: companyId } = useParams()

  const applicableStartDate = new Date(`${governmentAllocation.applicable_start_date} `)
  const applicableEndDate = new Date(`${governmentAllocation.applicable_end_date} `)
  const isActualMonth = isSameMonth(new Date(year, month), new Date())

  const showGovernmentAllocation = useMemo(
    () =>
      isSameMonth(applicableStartDate, new Date(year, month)) ||
      isWithinInterval(new Date(year, month), {
        start: new Date(`${governmentAllocation.applicable_start_date} `),
        end: new Date(`${governmentAllocation.applicable_end_date} `),
      }),
    [governmentAllocation.id, governmentAllocation.applicable_start_date, governmentAllocation.applicable_end_date, year],
  )

  const isInPeriod = useMemo(
    () =>
      isWithinInterval(new Date(), {
        start: new Date(governmentAllocation?.applicable_start_date),
        end: new Date(governmentAllocation?.applicable_end_date),
      }),
    [governmentAllocation?.applicable_start_date, governmentAllocation?.applicable_end_date, year],
  )

  const isExpired = useMemo(
    () => isAfter(new Date(), new Date(governmentAllocation?.applicable_end_date)),
    [governmentAllocation?.applicable_end_date, year],
  )

  const governmentAllocationStatus = useMemo(() => {
    if (governmentAllocation.status === 'pending') {
      return 'pending'
    }

    if (isInPeriod) {
      return 'active'
    }

    if (isExpired) {
      const hasRemainingCredit = Number(governmentAllocation.remaining_total) > 0

      return hasRemainingCredit ? 'expired_with_credit' : 'expired'
    }

    return 'active_not_in_period'
  }, [governmentAllocation.status, isInPeriod, isExpired, year])

  return (
    <>
      <div className="relative h-[120px]">
        <div
          className={cn(
            'absolute flex items-center w-[calc(100%+1px)] h-[120px] overflow-hidden',
            month === 11 && 'w-full',
          )}
        >
          {showGovernmentAllocation && (
            <Link
              to={
                !['completed', 'active'].includes(governmentAllocation.main_entry.status)
                  ? `/companies/${companyId}/government-allocations/${governmentAllocation.main_entry.id}/edit`
                  : `/companies/${companyId}/government-allocations/${governmentAllocation.id}`
              }
              data-tooltip-id={`${governmentAllocation.id}-${year}-${month}`}
              className={cn(
                'absolute rounded-lg border-2 border-transparent overflow-hidden cursor-pointer opacity-90',
                companyGovernmentAllocationStatusStyle(governmentAllocation)[
                  governmentAllocationStatus as keyof typeof companyGovernmentAllocationStatusStyle
                ],
              )}
              style={{
                left: getCardLeftPosition({ applicableStartDate, year, month }),
                right: getCardRightPosition({ applicableEndDate, year, month }),
              }}
              data-testid="government_allocation_month_details_card"
            >
              <GovernmentAllocationCardDetails governmentAllocation={governmentAllocation} isExpired={isExpired} />
            </Link>
          )}

          {isActualMonth && (
            <div
              className="absolute top-0 bottom-0 w-px bg-yellow-600 -translate-x-px"
              style={{ left: getBarLeftPosition() }}
            />
          )}
        </div>
      </div>

      <Tooltip
        id={`${governmentAllocation.id}-${year}-${month}`}
        place="right"
        arrowColor="transparent"
        className={cn(
          'rounded-lg border-2 border-transparent p-0 z-10',
          companyGovernmentAllocationStatusStyle[
            governmentAllocationStatus as keyof typeof companyGovernmentAllocationStatusStyle
          ],
        )}
      >
        <GovernmentAllocationCardDetails governmentAllocation={governmentAllocation} hover isExpired={isExpired} />
      </Tooltip>
    </>
  )
}
